import { displayUsername } from '../../utils/userUtils';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { Shift } from '../../interfaces/shift/Shift';
import { RosterCell } from '../../interfaces/shift/RosterCell';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { ShiftRosterService } from '../../services/shift-roster.service';
import { ShiftService } from '../../services/shift.service';
import { UserService } from '../../services/user.service';
import RosterCellOverrideDialog from './RosterCellOverrideDialog';
import BulkSwapDialog from './BulkSwapDialog';

import SearchableSelect from '../common/SearchableSelect';
interface RowUser {
    id: string;
    username: string;
    name?: string;
}

const PAGE_SIZE = 50;
const MAX_DAYS = 31;

const todayStr = () => toLocalIsoDate();
const addDays = (dateStr: string, n: number): string => {
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() + n);
    return toLocalIsoDate(d);
};

// Generate array of date strings between from and to (inclusive)
const buildDateRange = (from: string, to: string): string[] => {
    const dates: string[] = [];
    let cur = from;
    while (cur <= to) {
        dates.push(cur);
        cur = addDays(cur, 1);
    }
    return dates;
};

// Day-of-week short label
const dayLabel = (dateStr: string): string => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[new Date(dateStr + 'T00:00:00').getDay()];
};

type StatusColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const statusChipColor = (status: string): StatusColor => {
    switch (status) {
        case 'SCHEDULED':  return 'default';
        case 'WORKED':     return 'success';
        case 'ABSENT':     return 'error';
        case 'ON_LEAVE':   return 'warning';
        case 'HOLIDAY':    return 'info';
        case 'CANCELLED':  return 'secondary';
        default:           return 'default';
    }
};

const ShiftRosterCalendar: React.FC = () => {
    const { selectedInstitutionId } = useAuth();
    const toast = useToast();

    // POST /roster/regenerate is guarded by SHIFT_OVERRIDE_INSTANCE (not the view keys), so a
    // roster-view-only caller (Department Head) must not be offered the button.
    const canOverride   = usePermission(PERMISSIONS.SHIFT_OVERRIDE_INSTANCE);
    const canRegenerate = canOverride;
    // Both hooks called unconditionally: `a() || b()` would skip the second hook whenever the first
    // is true, changing the hook count between renders once permissions load.
    const canViewShifts = usePermission(PERMISSIONS.SHIFT_VIEW);
    const canViewRoster = usePermission(PERMISSIONS.SHIFT_ROSTER_VIEW);
    const canView       = canViewShifts || canViewRoster;

    const [fromDate, setFromDate] = useState(todayStr());
    const [toDate, setToDate]     = useState(addDays(todayStr(), 6));
    const [shiftFilter, setShiftFilter] = useState('');
    const [userSearch, setUserSearch]   = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [shifts, setShifts]       = useState<Shift[]>([]);
    const [users, setUsers]         = useState<RowUser[]>([]);
    const [rosterCells, setRosterCells] = useState<RosterCell[]>([]);
    const [loading, setLoading]     = useState(false);
    const [rangeError, setRangeError] = useState('');

    const [overrideCell, setOverrideCell]   = useState<RosterCell | null>(null);
    const [overrideOpen, setOverrideOpen]   = useState(false);
    const [bulkSwapOpen, setBulkSwapOpen]   = useState(false);
    const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleSearchChange = (val: string) => {
        setUserSearch(val);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setDebouncedSearch(val);
            setPage(0);
        }, 400);
    };

    // Column array derived from date range
    const dateColumns = useMemo(() => {
        if (!fromDate || !toDate || fromDate > toDate) return [];
        const diff = (new Date(toDate).getTime() - new Date(fromDate).getTime()) / 86400000;
        if (diff > MAX_DAYS) return [];
        return buildDateRange(fromDate, toDate);
    }, [fromDate, toDate]);

    // Validate range
    const validateRange = useCallback((): boolean => {
        if (!fromDate || !toDate) { setRangeError('Both dates are required'); return false; }
        if (fromDate > toDate)    { setRangeError('From date must be on or before To date'); return false; }
        const diff = (new Date(toDate).getTime() - new Date(fromDate).getTime()) / 86400000;
        // diff > 31 means 32+ days (more than 31-day window)
        if (diff > MAX_DAYS) { setRangeError(`Date range cannot exceed ${MAX_DAYS} days`); return false; }
        setRangeError('');
        return true;
    }, [fromDate, toDate]);

    const fetchData = useCallback(async () => {
        if (!selectedInstitutionId) return;
        if (!validateRange()) return;
        setLoading(true);
        try {
            const [pageData, allShifts, cells] = await Promise.all([
                UserService.getInstitutionUsers(page, PAGE_SIZE, selectedInstitutionId, debouncedSearch || undefined, true),
                ShiftService.list(selectedInstitutionId),
                ShiftRosterService.getRoster(selectedInstitutionId, fromDate, toDate, undefined, shiftFilter || undefined),
            ]);
            const content: RowUser[] = (pageData.content || []).map((u: any) => ({
                id: u.id,
                username: u.username,
                name: u.fullName || u.name || undefined,
            }));
            setUsers(content);
            setTotalElements(pageData.totalElements || 0);
            setShifts(allShifts);
            setRosterCells(cells);
        } catch (err) {
            toast.showApiError(err, 'Failed to load roster');
        } finally {
            setLoading(false);
        }
    }, [selectedInstitutionId, page, debouncedSearch, fromDate, toDate, shiftFilter, validateRange, toast]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchData]);

    // O(1) cell lookup: Map<userId, Map<businessDate, RosterCell>>
    const cellMap = useMemo(() => {
        const m = new Map<string, Map<string, RosterCell>>();
        rosterCells.forEach(cell => {
            if (!m.has(cell.userId)) m.set(cell.userId, new Map());
            m.get(cell.userId)!.set(cell.businessDate, cell);
        });
        return m;
    }, [rosterCells]);

    const handleRegenerate = async () => {
        setConfirmRegenerateOpen(false);
        if (!selectedInstitutionId || !validateRange()) return;
        try {
            const result = await ShiftRosterService.regenerate(selectedInstitutionId, fromDate, toDate);
            toast.showSuccess(`Regenerated ${result.generated} instance(s)`);
            await fetchData();
        } catch (err) {
            toast.showApiError(err, 'Regenerate failed');
        }
    };

    const handleCellClick = (cell: RosterCell | undefined) => {
        if (!cell || !canOverride) return;
        setOverrideCell(cell);
        setOverrideOpen(true);
    };

    const handleOverrideSaved = () => {
        setOverrideOpen(false);
        setOverrideCell(null);
        fetchData();
    };

    const handleBulkSwapSaved = () => {
        setBulkSwapOpen(false);
        fetchData();
    };

    if (!canView) {
        return <Typography sx={{ p: 3 }}>You do not have permission to view the shift roster.</Typography>;
    }

    return (
        <Box data-help-page="shifts.roster.page" sx={{ padding: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Shift Roster</Typography>
                <Stack direction="row" spacing={1}>
                    {canOverride && (
                        <Button
                            variant="outlined"
                            onClick={() => setBulkSwapOpen(true)}
                            data-help="shifts.roster.bulkSwap"
                            data-testid="bulk-swap-btn"
                        >
                            Bulk Swap
                        </Button>
                    )}
                    {canRegenerate && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setConfirmRegenerateOpen(true)}
                            data-help="shifts.roster.regenerate"
                            data-testid="regenerate-btn"
                        >
                            Regenerate
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Filters */}
            <Box data-help-section="shifts.roster.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                    data-help="shifts.roster.from"
                    label="From"
                    type="date"
                    size="small"
                    value={fromDate}
                    onChange={e => { setFromDate(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'data-testid': 'filter-from' }}
                    sx={{ flex: '1 1 150px', maxWidth: 200 }}
                />
                <TextField
                    data-help="shifts.roster.to"
                    label="To"
                    type="date"
                    size="small"
                    value={toDate}
                    onChange={e => { setToDate(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'data-testid': 'filter-to' }}
                    sx={{ flex: '1 1 150px', maxWidth: 200 }}
                />
                <SearchableSelect
                    options={shifts}
                    value={shiftFilter}
                    onChange={id => { setShiftFilter(id); setPage(0); }}
                    getOptionLabel={s => `${s.name} (${s.code})`}
                    label="Shift"
                    placeholder="All shifts"
                    data-testid="filter-shift"
                    data-help="shifts.roster.shiftFilter"
                    sx={{ flex: '1 1 180px', maxWidth: 260 }}
                />
                <TextField
                    data-help="shifts.roster.search"
                    label="Search user"
                    size="small"
                    value={userSearch}
                    onChange={e => handleSearchChange(e.target.value)}
                    inputProps={{ 'data-testid': 'filter-user-search' }}
                    sx={{ flex: '1 1 180px', maxWidth: 260 }}
                />
            </Box>

            {rangeError && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="range-error">
                    {rangeError}
                </Alert>
            )}

            {/* Grid */}
            {loading ? (
                <Typography>Loading...</Typography>
            ) : (
                <TableContainer data-help-section="shifts.roster.table" component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="shifts.roster.col.user" sx={{ minWidth: 160, fontWeight: 600, position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                                    User
                                </TableCell>
                                {dateColumns.map(d => (
                                    <TableCell key={d} data-help="shifts.roster.col.day" align="center" sx={{ minWidth: 90, fontWeight: 600 }}>
                                        <Box>{d.slice(5)}</Box>
                                        <Box sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{dayLabel(d)}</Box>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={dateColumns.length + 1}>No users found</TableCell>
                                </TableRow>
                            ) : (
                                users.map(u => (
                                    <TableRow key={u.id} data-testid={`roster-row-${u.id}`}>
                                        <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                            <Box>{u.name || displayUsername(u.username)}</Box>
                                        </TableCell>
                                        {dateColumns.map(d => {
                                            const cell = cellMap.get(u.id)?.get(d);
                                            return (
                                                <TableCell
                                                    key={d}
                                                    data-help="shifts.roster.cell"
                                                    align="center"
                                                    sx={{ cursor: cell && canOverride ? 'pointer' : 'default', p: 0.5 }}
                                                    onClick={() => handleCellClick(cell)}
                                                    data-testid={`roster-cell-${u.id}-${d}`}
                                                >
                                                    {cell ? (
                                                        <Tooltip title={`${cell.status}${cell.isManualOverride ? ' (manual override)' : ''}`}>
                                                            <Chip
                                                                label={`${cell.shiftCode ?? '?'}${cell.isManualOverride ? ' ★' : ''}`}
                                                                color={statusChipColor(cell.status)}
                                                                size="small"
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography variant="body2" color="text.disabled">—</Typography>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalElements}
                        page={page}
                        onPageChange={(_e, newPage) => setPage(newPage)}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                    />
                </TableContainer>
            )}

            <Dialog open={confirmRegenerateOpen} onClose={() => setConfirmRegenerateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Regenerate roster</DialogTitle>
                <DialogContent>
                    <Typography>
                        Regenerate the roster from {fromDate} to {toDate}? Shifts will be re-created from the
                        current assignments and rotation patterns.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRegenerateOpen(false)}>Cancel</Button>
                    <Button onClick={handleRegenerate} variant="contained" data-help="shifts.roster.regenerateConfirm" data-testid="regenerate-confirm-btn">Regenerate</Button>
                </DialogActions>
            </Dialog>

            {/* Override dialog */}
            {overrideCell && selectedInstitutionId && (
                <RosterCellOverrideDialog
                    open={overrideOpen}
                    onClose={() => { setOverrideOpen(false); setOverrideCell(null); }}
                    cell={overrideCell}
                    institutionId={selectedInstitutionId}
                    shifts={shifts}
                    onSaved={handleOverrideSaved}
                />
            )}

            {/* Bulk swap dialog */}
            {selectedInstitutionId && (
                <BulkSwapDialog
                    open={bulkSwapOpen}
                    onClose={() => setBulkSwapOpen(false)}
                    institutionId={selectedInstitutionId}
                    users={users}
                    onSaved={handleBulkSwapSaved}
                />
            )}
        </Box>
    );
};

export default ShiftRosterCalendar;
